import { useMemo } from "react";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DemandAdjustmentTargets } from "../../types/demand-factor.type";
import { Branch } from "../../types/user.type";
import { GENERAL_CUSTOMER_GROUP_PREFIX } from "./constants";
import CodeMultiSelect, { CodeOption } from "./CodeMultiSelect";
import { DemandFactorLookups, NumberedRoute } from "./useDemandFactorLookups";

interface TargetFieldsProps {
  targets: DemandAdjustmentTargets;
  onChange: (patch: Partial<DemandAdjustmentTargets>) => void;
  lookups: DemandFactorLookups;
  canSelectBranch: boolean;
  /** A branch manager's own branch, which its rows are locked to. */
  ownBranchCode: string | null;
}

const FIELD_SIZE = { xs: 12, md: 6 };

/**
 * The routes of the picked branches. Nothing picked means every branch, so every route. A
 * branch manager's route list is already its own branch's alone.
 */
const routesOfBranches = (
  routes: NumberedRoute[],
  branches: Branch[],
  branchCodes: string[],
): NumberedRoute[] => {
  if (branchCodes.length === 0) return routes;
  const branchUuids = new Set(
    branches
      .filter((branch) => branchCodes.includes(branch.branch_code))
      .map((branch) => branch.uuid),
  );
  return routes.filter((route) => branchUuids.has(route.branch_uuid));
};

/** One multi-select per target level: OR inside a level, AND across levels. */
export default function TargetFields({
  targets,
  onChange,
  lookups,
  canSelectBranch,
  ownBranchCode,
}: TargetFieldsProps) {
  const { branches, routes, groups, channels, productOptions } = lookups;

  const branchOptions = useMemo<CodeOption[]>(() => {
    if (canSelectBranch) {
      return branches.map((branch) => ({
        code: branch.branch_code,
        label: `${branch.branch_code} - ${branch.branch_name}`,
      }));
    }
    return ownBranchCode ? [{ code: ownBranchCode, label: ownBranchCode }] : [];
  }, [canSelectBranch, branches, ownBranchCode]);

  const selectableRoutes = useMemo(
    () =>
      canSelectBranch ? routesOfBranches(routes, branches, targets.target_branch) : routes,
    [canSelectBranch, routes, branches, targets.target_branch],
  );

  const routeOptions = useMemo<CodeOption[]>(
    () =>
      selectableRoutes.map((route) => ({
        code: route.route_number,
        label: `${route.route_number} - ${route.route_name}`,
      })),
    [selectableRoutes],
  );

  const groupOptions = useMemo<CodeOption[]>(
    () => groups.map((group) => ({ code: group.code, label: group.name })),
    [groups],
  );

  const channelOptions = useMemo<CodeOption[]>(
    () => channels.map((channel) => ({ code: channel.code, label: channel.name })),
    [channels],
  );

  // Routes outside the newly picked branches are dropped with them: the API refuses a route
  // that is not in the row's branches.
  const handleBranchChange = (branchCodes: string[]) => {
    const allowedRoutes = new Set(
      routesOfBranches(routes, branches, branchCodes).map((route) => route.route_number),
    );
    onChange({
      target_branch: branchCodes,
      target_route: targets.target_route.filter((routeNumber) => allowedRoutes.has(routeNumber)),
    });
  };

  const handleRouteChange = (routeNumbers: string[]) => {
    onChange({ target_route: routeNumbers });
  };

  const handleGroupChange = (groupCodes: string[]) => {
    onChange({ target_customer_group: groupCodes });
  };

  // "เชนทั้งหมด" is every named chain — every group except ลูกค้าทั่วไป (R5).
  const handleSelectAllChains = () => {
    onChange({
      target_customer_group: groups
        .filter((group) => group.prefix !== GENERAL_CUSTOMER_GROUP_PREFIX)
        .map((group) => group.code),
    });
  };

  const handleChannelChange = (channelCodes: string[]) => {
    onChange({ target_channel: channelCodes });
  };

  const handleExcludeChannelChange = (channelCodes: string[]) => {
    onChange({ exclude_channel: channelCodes });
  };

  const handleProductChange = (productCodes: string[]) => {
    onChange({ target_product_code: productCodes });
  };

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">เป้าหมาย</Typography>
      <Typography variant="caption" color="text.secondary">
        ในช่องเดียวกันเลือกได้หลายค่า (ตรงค่าใดค่าหนึ่งก็ได้) · ข้ามช่องต้องตรงทุกช่อง · ช่องที่ว่าง = ทั้งหมด
      </Typography>
      <Grid container spacing={2} mt={0.5}>
        <Grid size={FIELD_SIZE}>
          <CodeMultiSelect
            label="สาขา"
            options={branchOptions}
            value={targets.target_branch}
            onChange={handleBranchChange}
            disabled={!canSelectBranch}
            helperText={canSelectBranch ? "ว่าง = ทุกสาขา" : "ล็อกเป็นสาขาของคุณ"}
          />
        </Grid>
        <Grid size={FIELD_SIZE}>
          <CodeMultiSelect
            label="สายส่ง"
            options={routeOptions}
            value={targets.target_route}
            onChange={handleRouteChange}
            helperText="ว่าง = ทุกสาย · แสดงเฉพาะสายของสาขาที่เลือก"
          />
        </Grid>
        <Grid size={FIELD_SIZE}>
          <Stack gap={0.5}>
            <CodeMultiSelect
              label="กลุ่มลูกค้า"
              options={groupOptions}
              value={targets.target_customer_group}
              onChange={handleGroupChange}
              helperText="ว่าง = ทุกกลุ่ม"
            />
            <Stack direction="row">
              <Button size="small" onClick={handleSelectAllChains}>
                เชนทั้งหมด (ไม่ใช่ Z)
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid size={FIELD_SIZE}>
          <CodeMultiSelect
            label="ช่องทาง"
            options={channelOptions}
            value={targets.target_channel}
            onChange={handleChannelChange}
            helperText="ว่าง = ทุกช่องทาง"
          />
        </Grid>
        <Grid size={FIELD_SIZE}>
          <CodeMultiSelect
            label="ยกเว้นช่องทาง"
            options={channelOptions}
            value={targets.exclude_channel}
            onChange={handleExcludeChannelChange}
            helperText="ว่าง = ไม่ยกเว้น · ร้านที่ไม่มีช่องทางไม่ถูกยกเว้น"
          />
        </Grid>
        <Grid size={FIELD_SIZE}>
          <CodeMultiSelect
            label="สินค้า"
            options={productOptions}
            value={targets.target_product_code}
            onChange={handleProductChange}
            helperText="ว่าง = ทุกสินค้า"
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
